import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useGetUserProfileQuery } from "../../../../../api/userAuthApi";
import { areAddressesEqual } from "../../../../../../../common/helpers/addressHelpers";
import ProfileBillingForm from "./ProfileBillingForm";
import ProfileShippingForm from "./ProfileShippingForm";

type ActiveTab = "billing" | "shipping";

const AddressesTab: React.FC = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<ActiveTab>("billing");
  const { data: profile } = useGetUserProfileQuery();

  // State for the checkbox is managed here, in the parent component.
  const [useBillingForShipping, setUseBillingForShipping] = useState(true);

  // Ref to track initialization status
  const isStateInitialized = useRef(false);

  // Initialize and sync the checkbox state based on actual address data.
  useEffect(() => {
    if (profile && !isStateInitialized.current) {
      // Use the shared helper for robust comparison
      const areAddressesSame = areAddressesEqual(
        profile.billingAddress,
        profile.shippingAddress
      );

      setUseBillingForShipping(areAddressesSame);
      isStateInitialized.current = true;
    }
  }, [profile]);

  const handleToggle = (isChecked: boolean) => {
    setUseBillingForShipping(isChecked);
  };

  return (
    <div className="profile-addresses-tab">
      {/* Tab Navigation */}
      <div className="profile-addresses-tab__nav" role="tablist">
        <button
          className={`profile-addresses-tab__tab-btn ${
            activeTab === "billing" ? "is-active" : ""
          }`}
          onClick={() => setActiveTab("billing")}
          role="tab"
          aria-selected={activeTab === "billing"}
          aria-controls="billing-address-panel"
        >
          {t("user.addresses.billingTitle", "Billing Address")}
        </button>
        <button
          className={`profile-addresses-tab__tab-btn ${
            activeTab === "shipping" ? "is-active" : ""
          }`}
          onClick={() => setActiveTab("shipping")}
          role="tab"
          aria-selected={activeTab === "shipping"}
          aria-controls="shipping-address-panel"
          disabled={useBillingForShipping}
        >
          {t("user.addresses.shippingTitle", "Shipping Address")}
        </button>
      </div>

      {/* Tab Content */}
      <div className="profile-addresses-tab__content">
        <div
          id="billing-address-panel"
          role="tabpanel"
          hidden={activeTab !== "billing"}
        >
          {activeTab === "billing" && (
            <ProfileBillingForm
              useBillingForShipping={useBillingForShipping}
              setUseBillingForShipping={handleToggle}
            />
          )}
        </div>
        <div
          id="shipping-address-panel"
          role="tabpanel"
          hidden={activeTab !== "shipping"}
        >
          {activeTab === "shipping" && !useBillingForShipping && (
            <ProfileShippingForm />
          )}
        </div>
      </div>
    </div>
  );
};

export default AddressesTab;
